import {CommonModule} from '@angular/common';
import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {PacDetailComponent} from '../../components/pac-detail/pac-detail.component';
import {Router, ActivatedRoute} from '@angular/router';
import {ConfigurationService} from '../../shared/services/configuration.service';
import {AdditionalPACData, PaidPaymentCollection, PaymentCategory, PaymentFrequency} from '../../models/additional-pac-data.model';
import {PaymentCollectionDetailsComponent} from '../../components/payment-collection-details/payment-collection-details.component';
import {PaymentMethodComponent} from '../../components/payment-method/payment-method.component';
import {CustomModalComponent} from '../../components/custom-modal/custom-modal.component';
import { VirtualKeyboardComponent} from '../../components/virtual-keyboard/virtual-keyboard.component';
import { PacService } from '../../shared/services/pac.service';
import { PaymentBlockService } from '../../services/payment-block.service';
import { AppConstants } from '../../shared/app-constants';
import { ErrorBannerComponent } from '../../components/error-banner/error-banner.component';
import {PaymentErrorService} from '../../shared/services/payment-error-service';
import {SuccessBannerComponent} from '../../components/success-banner/success-banner.component';

@Component({
  selector: 'app-court-payment',
  imports: [
    CommonModule,
    PacDetailComponent,
    PaymentCollectionDetailsComponent,
    PaymentMethodComponent,
    CustomModalComponent,
    VirtualKeyboardComponent,
    ErrorBannerComponent,
    SuccessBannerComponent
  ],
  templateUrl: './court-payment.component.html',
  styleUrl: './court-payment.component.scss',
  standalone: true,
})
export class CourtPaymentComponent implements OnInit, OnDestroy {
  pacDetails!: AdditionalPACData;
  anyPaymentProcessed: boolean = false;
  creditPaymentDetails!: PaidPaymentCollection;
  isAborted: boolean = false;
  isPayNowTransactionMissing: boolean = false;
  payNowAttempted: number = 0;
  errorMessage: string = '';
  successMessage: string = '';
  hasPaymentModes: boolean = false;
  private readonly clearTimeoutId: any = null;
  private readonly isTimerActive: boolean = false;
  private blockResolvedSub!: Subscription;


  @ViewChild('abortModalRef') abortModalRef!: CustomModalComponent;

  constructor(private readonly router: Router, private readonly configService: ConfigurationService,
              private readonly pacService: PacService, private readonly route: ActivatedRoute,
              private readonly paymentErrorService: PaymentErrorService,
              private readonly paymentBlockService: PaymentBlockService
  ) {
  }

  ngOnInit(): void {
    this.blockResolvedSub = this.paymentBlockService.blockResolved$.subscribe(data => {
      if (data?.pacNumber && data.pacNumber === this.pacDetails?.pacNumber) {
        this.assignPacDetails(data);
      }
    });

    // First check if pacDetails exists in history.state
    const paymentDetails = history.state?.pacDetails;

    if (paymentDetails) {
      this.fetchPACDetails(paymentDetails.pacNumber);
      this.assignPacDetails(paymentDetails)
    } else {
      // Possibly backend redirect with query params
      this.route.queryParams.subscribe(params => {
        const pacReference = params['pacReference'];
        const status = params['status'];
        const amount = params['amount'];
        const transactionId = params['transactionId'];
        const responseCode = params['reasonCode'];
        const messageCode = params['messageCode'];
        const retryAfterSeconds = Number(params['retryAfterSeconds']);

        if (messageCode === 'payment_processing' && retryAfterSeconds > 0) {
          this.paymentBlockService.notifyBlocked(pacReference, retryAfterSeconds);
        } else {
          this.paymentErrorService.getMessage(responseCode, status).subscribe(message => {
            if (status === 'failure') {
              this.errorMessage = message;
            } else {
              this.successMessage = message;
            }
          });
        }
        if (pacReference) {
          this.creditPaymentDetails = {
            paymentMode: 'CREDIT_CARD',
            bankReference: '',
            paymentAmount: Number.isNaN(Number(amount)) ? 0 : Number(amount),
            bankCode: transactionId, // Need to confirm if this correct?
            transactionId,
            status: params['status'],
            message: AppConstants.getDisplayMessage(params['messageCode']),
            pacNumber: pacReference
          };
            this.fetchPACDetails(pacReference);
        }
      });
    }
  }

  fetchPACDetails(pacNumber: string) {
    this.pacService.fetchAdditionalInfo(pacNumber).subscribe({
      next: (additionalPaymentInfo: AdditionalPACData) => {
        this.assignPacDetails(additionalPaymentInfo);
      },
      error: (err) => {
      }
    });
  }

  assignPacDetails(data: AdditionalPACData): void {
    const selectedAmount = data.selectedAmountPayable ?? 0;
    const amountPaid = data.amountPaid ?? 0;
    this.pacDetails = data;
    this.pacDetails.payerId = data.payerId;
    this.pacDetails.totalAmountPayable = this.pacDetails.paymentFrequency === PaymentFrequency.I ? selectedAmount : data.totalAmountPayable;
    this.pacDetails.balancePayable = this.pacDetails.paymentFrequency === PaymentFrequency.I ? selectedAmount - amountPaid: data.balancePayable;
    this.pacDetails.paymentCategory = PaymentCategory.COURT_PAYMENT;
    this.pacDetails.paidLineItems ??= [];
    if (data.balancePayable == data.totalAmountPayable && this.pacDetails.paidLineItems.length == 0) {
      this.anyPaymentProcessed = false;
    } else {
      this.anyPaymentProcessed = true;
    }
  }

  onClickHomeButton() {
    if (!this.pacDetails?.pacNumber) {
      this.redirectToHomePage();
      return;
    }
    this.pacService.fetchAdditionalInfo(this.pacDetails.pacNumber).subscribe({
      next: (additionalPaymentInfo: AdditionalPACData) => {
        this.assignPacDetails(additionalPaymentInfo);
        if (this.hasPaymentModes) {
          this.abortModalRef.showModal();
        } else {
          this.redirectToHomePage();
        }
      },
      error: (err) => {
      }
    });  }

  handleAbortModalClose(confirmed: boolean) {
    this.abortModalRef.hideModal();
    if (confirmed) {
      if (this.anyPaymentProcessed) {
        this.redirectToSuccessPaymentPage();
      } else {
        this.redirectToHomePage();
      }
    }
  }

  onPacDetailsUpdated(pacDetails : AdditionalPACData) {
    this.assignPacDetails(pacDetails)
  }

  redirectToSuccessPaymentPage() {
      this.partialPaymentAbort();
      this.payNowAttemptedCheck();
    this.router.navigate(['payment-success'], {
      state: { pacDetails: this.pacDetails, isAborted: this.isAborted, isPayNowTransactionMissing: this.isPayNowTransactionMissing, payNowAttempted: this.payNowAttempted}
    });
  }

  partialPaymentAbort() {
    const pacDetails = this.pacDetails;
    if (pacDetails.paymentFrequency === PaymentFrequency.O) {
      if (pacDetails.totalAmountPayable !== pacDetails.balancePayable && pacDetails.balancePayable !== 0) {
        this.isAborted = true;
      }
    } else {
      const remaining = pacDetails.selectedAmountPayable && (pacDetails.selectedAmountPayable - pacDetails.amountPaid);
      if (remaining && (remaining > 0 && remaining !== pacDetails.selectedAmountPayable)) {
        this.isAborted = true;
      }
    }
  }

  payNowAttemptedCheck() {
    const attemptKey = 'PAY_NOW_ATTEMPTED';
    this.payNowAttempted = Number(localStorage.getItem(attemptKey)) || 0;
    const payNowTransactionsFoundCount = this.pacDetails.payNowCompletedCount;
    if (payNowTransactionsFoundCount != undefined && payNowTransactionsFoundCount < this.payNowAttempted) {
      this.isPayNowTransactionMissing = true;
    }
  }

  redirectToHomePage() {
    this.router.navigate(['']);
  }

  get selectedPacCount(): number {
    return history.state?.pacDetails?.selectedPacNumbers?.length ?? 1;
  }

  onValidPaymentMethods(available: boolean): void {
    this.hasPaymentModes = available;
  }

  @HostListener('window:click')
  @HostListener('window:keydown')
  handleClearMessageTrigger(): void {
    this.paymentErrorService.clearMessagesWithDelay(() => {
      this.errorMessage = '';
      this.successMessage = '';
    }, AppConstants.MESSAGE_CLEAR_DELAY_MS);
  }

  ngOnDestroy(): void {
    this.blockResolvedSub?.unsubscribe();
  }

}
